import { useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { LoaderFunction, redirect, useNavigate } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { LanguageSwitcher } from "@/core/i18n/LanguageSwitcher/LanguageSwitcher";
import { getPlatform, getPlatformLocation, getUser } from "api/methods";
import { Icon } from "atoms/icon/Icon";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import ChevronRight from "atoms/icon/icons/ic_chevron_right_small_ol.svg";
import { SettingsContainer } from "molecules/containers/settings-container/SettingsContainer";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import Services from "services/Services";
import { waitObj } from "toolbox/Promise";
import { handleLinkClick } from "utils/androidByPassDeepLinkClick";
import { formatPhoneNumber } from "toolbox/PhoneNumber";

import { type AccountLink, useAccountLinks } from "./useAccountLinks";

const load = async () => {
  const platform = await queryClient.fetchQuery({ ...getPlatform.keyed() });
  await queryClient.fetchQuery({
    ...getUser.keyed(),
    staleTime: 1000 * 60 * 2,
  });

  const platformLocation = queryClient.fetchQuery({ ...getPlatformLocation.keyed() });

  return waitObj({
    platform,
    platformLocation,
  });
};

export const accountLoader: LoaderFunction = async () => {
  if (await Services.auth.isAuthorized()) return load();
  return redirect("/signin");
};

export default function AccountPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const anchorRef = useRef(null);
  const links = useAccountLinks();

  const { data: user } = useQuery({
    ...getUser.keyed(),
    staleTime: 1000 * 60 * 2,
  });

  const renderHeader = () => (
    <TopNav
      startIconSVG={ChevronLeft}
      onStartIconClick={() => {
        navigate(-1);
      }}
      showHeaderItems={false}
      className="desk:hidden"
    >
      <div className="flex max-h-[32px] items-center">
        <div className="flex w-full items-center justify-center text-sub2 uppercase text-primary">
          {t("account.header")}
        </div>
      </div>
    </TopNav>
  );

  const renderLink = (link: AccountLink) => {
    if (link.href) {
      return (
        <a
          key={link.key}
          ref={anchorRef}
          className="text-bold3 text-secondary transition-opacity active:opacity-60"
          href={link.href}
          target="_self"
          rel="noopener noreferrer"
          onClick={() => {
            handleLinkClick(anchorRef);
          }}
        >
          <div>
            <button className="flex w-full items-center gap-4 bg-white p-4 text-left text-body1 text-primary">
              <link.icon className="h-6 w-6 fill-[theme(textColor.secondary)]" />
              <div className="flex-1">{link.label}</div>
              <div>
                <Icon svg={ChevronRight} color="brandColor" size="xsmall" />
              </div>
            </button>
          </div>
        </a>
      );
    }

    return (
      <SettingsContainer
        key={link.key}
        to={link.to}
        startIconSVG={link.icon}
        onClick={link.onClick}
      >
        {link.label}
      </SettingsContainer>
    );
  };

  const linksIn = (group: AccountLink["group"]) => links.filter((link) => link.group === group);

  const contactRows = [user?.email, formatPhoneNumber(user?.phoneNumber)].filter(Boolean);

  return (
    <div className="bg-one">
      {renderHeader()}

      {/* Phone: the whole menu as a drill-down list. It is hidden on desktop,
          where the same entries live permanently in the rail beside this pane. */}
      <div className="mx-auto flex min-h-screen flex-col gap-2 bg-one px-4 py-6 desk:hidden">
        <Group>{linksIn("profile").map(renderLink)}</Group>
        <Group>
          {linksIn("main").map(renderLink)}
          <LanguageSwitcher />
        </Group>
        {linksIn("admin").length > 0 && <Group>{linksIn("admin").map(renderLink)}</Group>}
        <Group>{linksIn("logout").map(renderLink)}</Group>
      </div>

      {/* Desktop: the rail is the menu, so this pane says who is signed in
          rather than repeating the list. */}
      <div className="hidden desk:block">
        <h1 className="pb-2 font-serif text-h2 text-primary">
          {[user?.firstName, user?.lastName].filter(Boolean).join(" ")}
        </h1>
        {contactRows.map((row) => (
          <div key={row} className="text-body1 text-secondary">
            {row}
          </div>
        ))}
      </div>
    </div>
  );
}

const Group = ({ children }: { children: React.ReactNode }) => (
  <div className="divide-y divide-solid divide-background-two overflow-clip rounded">
    {children}
  </div>
);
